import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { notificationService } from '../api/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
export function useNotifications() {
    const { user, isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const { data: notifications = [], isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: notificationService.getNotifications,
        enabled: isAuthenticated,
    });
    const markAsReadMutation = useMutation({
        mutationFn: notificationService.markAsRead,
        onSuccess: (updatedNotification) => {
            queryClient.setQueryData(['notifications'], (old = []) => old.map((n) => (n._id === updatedNotification._id ? updatedNotification : n)));
        },
    });
    const markAllAsReadMutation = useMutation({
        mutationFn: notificationService.markAllAsRead,
        onSuccess: () => {
            queryClient.setQueryData(['notifications'], (old = []) => old.map((n) => ({ ...n, isRead: true })));
        },
    });
    useEffect(() => {
        if (!isAuthenticated || !user?.id)
            return;
        const socketUrl = import.meta.env.VITE_API_URL || window.location.origin;
        const socket = io(socketUrl, {
            query: { userId: user.id },
            transports: ['websocket'],
        });
        socket.on('connect', () => {
            console.log('[Socket] Connected for notifications');
        });
        socket.on('NEW_NOTIFICATION', (data) => {
            console.log('[Socket] Received new notification:', data);
            // Update local cache
            queryClient.setQueryData(['notifications'], (old = []) => {
                return [data, ...old];
            });
            // Show toast
            toast(data.payload.title, {
                description: data.payload.body,
                action: {
                    label: 'View',
                    onClick: () => {
                        // Logic to mark as read or navigate
                        markAsReadMutation.mutate(data._id);
                    },
                },
            });
        });
        return () => {
            socket.disconnect();
        };
    }, [isAuthenticated, user?.id, queryClient]);
    const unreadCount = notifications.filter((n) => !n.isRead).length;
    return {
        notifications,
        unreadCount,
        isLoading,
        markAsRead: markAsReadMutation.mutate,
        markAllAsRead: markAllAsReadMutation.mutate,
    };
}
